import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import css from './roadMap.module.css';
import Header from '../../components/standartComponents/header';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getCSRFToken, fetchCSRFToken } from '../../lib/csrf';

import { getTrialProgress } from '../../lib/trialLessonProgress';

// Purpose keys — keep translation keys here so UI strings go through i18n
const PURPOSES = [
  { id: 1, purpose: 'Travel', emoji: '✈️' },
  { id: 2, purpose: 'Work', emoji: '💼' },
  { id: 3, purpose: 'Study', emoji: '🎓' },
  { id: 4, purpose: 'Socializing', emoji: '💬' },
  { id: 5, purpose: 'For yourself', emoji: '🎵' },
];

// Реальні слова тягнуться з /api/v1/student-vocabulary/ (див. useEffect fetchVocabulary нижче),
// так само, як у WordsComponent.jsx.

/* ========== ICONS ========== */
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
);
const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
);
const ArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
);
const BookIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg>
);
const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
);
const InfoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
);
const DotsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/></svg>
);
const ChevronDown = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
);
const ChevronUp = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6"/></svg>
);
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
);
const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 18a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2"/><rect width="18" height="18" x="3" y="4" rx="2"/><circle cx="12" cy="10" r="2"/><line x1="8" x2="8" y1="2" y2="4"/><line x1="16" x2="16" y1="2" y2="4"/></svg>
);

const RoadMap = ({ authUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n, t } = useTranslation();

  const acceptLangHeaders = () => ({
    'Accept-Language': i18n.language || 'uk',
  });

  // Скидаємо кеш словника при зміні мови, щоб переклади підтягнулися заново
  useEffect(() => {
    vocabEntriesRef.current = null;
  }, [i18n.language]);

  // Читаємо level і purpose з query params
  const searchParams = new URLSearchParams(location.search);
  const levelParam = searchParams.get('level');
  const purposeParam = searchParams.get('purpose');

  // API стейт
  const [learningPath, setLearningPath] = useState(null);   // { id, name, level, purpose }
  const [pathDetails, setPathDetails] = useState(null);      // { topics: [...] }
  const [loadingPath, setLoadingPath] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState(null);

  // UI стейт
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [drawerSaving, setDrawerSaving] = useState(false);
  const [manageMode, setManageMode] = useState('');
  const [selLevel, setSelLevel] = useState(null);   // id обраної опції рівня (з drawerLevels)
  const [selTopic, setSelTopic] = useState(null);   // id обраної цілі (з drawerPurposes)
  const [topicStep, setTopicStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [planDrawerOpen, setPlanDrawerOpen] = useState(false);
  const [drawerLevels, setDrawerLevels] = useState([]);
  const [drawerPurposes, setDrawerPurposes] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [pendingRedirect, setPendingRedirect] = useState(null); // '/roadMap?level=..&purpose=..' після зміни плану
  const [wordsDrawerOpen, setWordsDrawerOpen] = useState(false);
  const [wordsDrawerLesson, setWordsDrawerLesson] = useState(null); // { num, title, id } обраного уроку
  const [wordsDrawerWords, setWordsDrawerWords] = useState([]);     // слова саме цього уроку, вже з перекладом
  const [wordsDrawerLoading, setWordsDrawerLoading] = useState(false);
  const [completedWordsCount, setCompletedWordsCount] = useState(0);
  const [completedWordsLoading, setCompletedWordsLoading] = useState(false);
  const vocabEntriesRef = useRef(null); // кеш усього словника користувача (щоб не тягнути повторно на кожен урок)
  const manageRef = useRef(null);

  const openPlanDrawer = (mode) => {
    setManageMode(mode);
    setSelLevel(null);
    setSelTopic(null);
    setTopicStep(1);
    setShowSuccess(false);
    setDropdownOpen(false);
    setPlanDrawerOpen(true);
  };

  const closePlanDrawer = () => {
    setPlanDrawerOpen(false);
    setShowSuccess(false);
  };

  // Підтягуємо реальні рівні з бека, коли відкривається шторка керування планом.
  // Цілі (purposes) окремого ендпоінта не мають — беремо локальний список PURPOSES.
  useEffect(() => {
    if (!planDrawerOpen) return;
    const fetchLevels = async () => {
      setDrawerLoading(true);
      try {
        const lvlRes = await fetch('/api/v1/language-level-questionnaire', {
          credentials: 'include',
          headers: acceptLangHeaders(),
        });
        if (!lvlRes.ok) throw new Error(`HTTP ${lvlRes.status}`);
        const lvlData = await lvlRes.json();
        setDrawerLevels(lvlData.results || []);
      } catch (err) {
        console.error('Error fetching levels:', err);
      } finally {
        setDrawerLoading(false);
      }
    };
    fetchLevels();
    setDrawerPurposes(PURPOSES);
  }, [planDrawerOpen, i18n.language]);

  // 0. Якщо в URL немає level/purpose — підставляємо першу комбінацію з профілю користувача або збереженого опитування
  useEffect(() => {
    if (levelParam && purposeParam) return; // параметри вже є — цим займається ефект нижче

    const resolveDefaultPlan = async () => {
      setLoadingPath(true);
      setError(null);
      
      // For unauthenticated users, try to use saved survey data
      if (!authUser?.isAuthenticated) {
        const savedSurvey = getTrialProgress('survey');
        if (savedSurvey?.selLevel && savedSurvey?.selGoal) {
          console.log('📦 Using saved survey data for unauthenticated user:', savedSurvey);
          navigate(`/roadMap?level=${savedSurvey.selLevel.id}&purpose=${savedSurvey.selGoal.purposeId}`, { replace: true });
          return;
        } else {
          // No saved survey - redirect to survey
          navigate('/survey', { replace: true });
          return;
        }
      }
 
      try {
        const profileRes = await fetch('/api/v1/user-profile/current', {
          credentials: 'include',
          headers: acceptLangHeaders(),
        });
        const profile = await profileRes.json();

        const firstLevelInt = profile.english_levels?.[0];
        const firstPurposeId = profile.english_purposes?.[0];

        if (firstLevelInt == null || firstPurposeId == null) {
          navigate('/mylearning', { replace: true });
          return;
        }

        // english_levels зберігає "сирі" ints рівня, а не id опції — знаходимо опцію,
        // до якої належить цей int, щоб отримати правильний level id для learning-paths
        const lvlRes = await fetch('/api/v1/language-level-questionnaire', {
          credentials: 'include',
          headers: acceptLangHeaders(),
        });
        const lvlData = lvlRes.ok ? await lvlRes.json() : { results: [] };
        const matchedLevel = (lvlData.results || []).find(opt => (opt.levels || []).includes(firstLevelInt));

        if (!matchedLevel) {
          navigate('/mylearning', { replace: true });
          return;
        }

        navigate(`/roadMap?level=${matchedLevel.id}&purpose=${firstPurposeId}`, { replace: true });
      } catch (err) {
        console.error('Error resolving default plan:', err);
        navigate('/mylearning', { replace: true });
      }
    };

    resolveDefaultPlan();
  }, [levelParam, purposeParam, i18n.language, authUser?.isAuthenticated]);

  // 1. Завантажити learning path за level+purpose
  useEffect(() => {
    if (!levelParam || !purposeParam) return; // немає параметрів — обробляється ефектом вище

    const fetchPath = async () => {
      setLoadingPath(true);
      setError(null);
      try {
        const res = await fetch(
          `/api/v1/learning-paths/?level=${levelParam}&purpose=${purposeParam}`,
          { credentials: 'include', headers: acceptLangHeaders() }
        );
        
        if (res.status === 403 || res.status === 401) {
          // For unauthenticated users, show course info but not lessons
          // If backend says 403, check if we have survey data to at least show hero section
          if (!authUser?.isAuthenticated) {
            const savedSurvey = getTrialProgress('survey');
            if (savedSurvey?.selLevel && savedSurvey?.selGoal) {
              console.log('🎯 Using saved survey for hero section (backend returned 403)');
              // Create minimal path structure for hero display
              const mockPath = {
                id: null, // no real ID since backend denied access
                name: `${savedSurvey.selGoal.titleKey} English`,
                level: savedSurvey.selLevel,
                purpose: {
                  id: savedSurvey.selGoal.purposeId,
                  purpose: savedSurvey.selGoal.titleKey
                }
              };
              setLearningPath(mockPath);
              setLoadingPath(false);
              return;
            }
          }
          setError('Будь ласка, увійдіть, щоб переглянути курс');
          setLoadingPath(false);
          return;
        }
        
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        console.log("path data:", data);

        if (!data.results?.length) {
          setError('Курс не знайдено для цього рівня та цілі');
          setLoadingPath(false);
          return;
        }

        setLearningPath(data.results[0]);
      } catch (err) {
        console.error('Error fetching learning path:', err);
        setError('Не вдалося завантажити курс');
      } finally {
        setLoadingPath(false);
      }
    };

    fetchPath();
  }, [levelParam, purposeParam, i18n.language, authUser?.isAuthenticated]);

  // 2. Завантажити деталі (topics + lessons) коли є id
  useEffect(() => {
    if (!learningPath?.id) {
      // No real ID means we don't have backend access (unauthenticated)
      setPathDetails(null);
      setLoadingDetails(false);
      return;
    }

    const fetchDetails = async () => {
      setLoadingDetails(true);
      try {
        const res = await fetch(
          `/api/v1/learning-paths/${learningPath.id}/`,
          { credentials: 'include', headers: acceptLangHeaders() }
        );
        
        if (res.status === 403 || res.status === 401) {
          // Backend denies access - no details available
          console.log('⚠️ Backend returned 403/401 for learning path details');
          setPathDetails(null);
          setLoadingDetails(false);
          return;
        }
        
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        setPathDetails(data);
      } catch (err) {
        console.error('Error fetching path details:', err);
        setPathDetails(null);
      } finally {
        setLoadingDetails(false);
      }
    };

    fetchDetails();
  }, [learningPath?.id, i18n.language]);

  // Флатуємо всі уроки з topics у плаский масив для таймлайну
  const allLessons = pathDetails?.topics?.flatMap(topic =>
    topic.lessons.map(lesson => ({
      ...lesson,
      topicName: topic.name,
      topicId: topic.id,
    }))
  ) || [];

  const progressByLessonId = {};
  (authUser?.videoLessonsAll || []).forEach(v => {
    if (v.video?.id != null) progressByLessonId[v.video.id] = v;
    if (v.id != null) progressByLessonId[v.id] = v;
  });

  // Статус уроку: done — якщо є запис і is_completed; current — перший незавершений; решта — locked
  const lessonsWithProgress = allLessons.map(lesson => {
    const record = progressByLessonId[lesson.id];
    return {
      ...lesson,
      isDoneRecord: !!record?.is_completed,
      score: record ? Math.round(record.progress_percentage || 0) : null,
    };
  });

  const firstIncompleteIdx = lessonsWithProgress.findIndex(l => !l.isDoneRecord);

  const lessonsWithStatus = lessonsWithProgress.map((lesson, i) => ({
    ...lesson,
    num: i + 1,
    status: lesson.isDoneRecord ? 'done' : (i === firstIncompleteIdx ? 'current' : 'locked'),
  }));

  const currentLesson = lessonsWithStatus.find(l => l.status === 'current');

  // Тягнемо весь словник користувача один раз (так само, як fetchStudentVocabularyEntries
  // у WordsComponent.jsx) і кешуємо в ref, щоб не рефетчити на кожне відкриття шторки.
  const getAllVocabulary = async () => {
    if (vocabEntriesRef.current) return vocabEntriesRef.current;
    let url = '/api/v1/student-vocabulary/';
    let allResults = [];
    while (url) {
      const res = await axios.get(url, {
        withCredentials: true,
        headers: acceptLangHeaders(),
      });
      if (res.data?.results) allResults = [...allResults, ...res.data.results];
      if (res.data?.next) {
        const nextUrl = new URL(res.data.next);
        url = nextUrl.pathname + nextUrl.search;
      } else {
        url = null;
      }
    }
    vocabEntriesRef.current = allResults;
    return allResults;
  };

  const openWords = async (lessonNum, lessonTitle, lessonId) => {
    setWordsDrawerLesson({ num: lessonNum, title: lessonTitle, id: lessonId });
    setWordsDrawerOpen(true);
    setWordsDrawerLoading(true);
    setWordsDrawerWords([]);

    try {
      const [allVocab, lessonRes] = await Promise.all([
        getAllVocabulary(),
        fetch(`/api/v1/videoLesson/${lessonId}/`, {
          credentials: 'include',
          headers: acceptLangHeaders(),
        }),
      ]);

      // Слова саме цього уроку — так само, як isWordFromThisLesson у WordsComponent:
      // звіряємо текст запису словника з транскрипцією конкретного відео, а не по id.
      const lessonData = lessonRes.ok ? await lessonRes.json() : null;
      const transcriptPhrases = (lessonData?.video?.split_transcription || []).map(p => p.trim().toLowerCase());
      const phraseSet = new Set(transcriptPhrases);
      const entriesForLesson = allVocab.filter(w => phraseSet.has((w.text || '').trim().toLowerCase()));

      // Переклад тягнемо так само, як fetchTranslationsForEntries — по одному слову через /translate/
      let csrfToken = getCSRFToken();
      if (!csrfToken) {
        await fetchCSRFToken();
        csrfToken = getCSRFToken();
      }

      const withTranslations = await Promise.all(
        entriesForLesson.map(async (w) => {
          try {
            const res = await axios.post(
              `/api/v1/videoLesson/${lessonId}/translate/`,
              { text: w.text },
              {
                headers: {
                  'X-CSRFToken': csrfToken,
                  'Content-Type': 'application/json',
                  ...acceptLangHeaders(),
                },
                withCredentials: true,
              }
            );
            return {
              ...w,
              translation: res.data?.translation || '',
              usage_example: res.data?.usage_example || '',
            };
          } catch (err) {
            return { ...w, translation: '', usage_example: '' };
          }
        })
      );

      setWordsDrawerWords(withTranslations);
    } catch (err) {
      console.error('Error loading lesson words:', err);
    } finally {
      setWordsDrawerLoading(false);
    }
  };

  const closeWords = () => {
    setWordsDrawerOpen(false);
    setWordsDrawerLesson(null);
    setWordsDrawerWords([]);
  };

  // Закриття dropdown по кліку зовні
  useEffect(() => {
    const handler = (e) => {
      if (manageRef.current && !manageRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // Кількість слів зі словника, які належать пройденим урокам плану —
  // та сама логіка звірки з транскрипцією, що й у шторці "Words", але агреговано
  // по всіх уроках зі статусом done, з дедуплікацією по id запису словника.
  useEffect(() => {
    const doneLessons = lessonsWithStatus.filter(l => l.status === 'done');

    if (doneLessons.length === 0) {
      setCompletedWordsCount(0);
      return;
    }

    let cancelled = false;

    const computeCount = async () => {
      setCompletedWordsLoading(true);
      try {
        const allVocab = await getAllVocabulary();
        const matchedIds = new Set();

        await Promise.all(doneLessons.map(async (lesson) => {
          try {
            const res = await fetch(`/api/v1/videoLesson/${lesson.id}/`, {
              credentials: 'include',
              headers: acceptLangHeaders(),
            });
            if (!res.ok) return;
            const data = await res.json();
            const phrases = (data?.video?.split_transcription || []).map(p => p.trim().toLowerCase());
            const phraseSet = new Set(phrases);
            allVocab.forEach(w => {
              if (phraseSet.has((w.text || '').trim().toLowerCase())) {
                matchedIds.add(w.id);
              }
            });
          } catch (err) {
            console.error('Error loading transcript for lesson', lesson.id, err);
          }
        }));

        if (!cancelled) setCompletedWordsCount(matchedIds.size);
      } catch (err) {
        console.error('Error computing completed words count:', err);
      } finally {
        if (!cancelled) setCompletedWordsLoading(false);
      }
    };

    computeCount();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathDetails, authUser?.videoLessonsAll, i18n.language]);

  // Зберігає ЗМІНУ поточного плану (заміна рівня і/або цілі), а не додавання нового
  const changePlan = async ({ newLevel, newPurpose }) => {
    setDrawerSaving(true);
    try {
      let csrfToken = getCSRFToken();
      if (!csrfToken) {
        await fetchCSRFToken();
        csrfToken = getCSRFToken();
      }

      const profileRes = await fetch('/api/v1/user-profile/current', {
        credentials: 'include',
        headers: acceptLangHeaders(),
      });
      const profile = await profileRes.json();

      let newLevels = [...(profile.english_levels || [])];
      let newPurposes = [...(profile.english_purposes || [])];

      // прибираємо ints поточного рівня, додаємо ints нового
      if (newLevel) {
        const oldLevelInts = learningPath?.level?.levels || [];
        newLevels = newLevels.filter(l => !oldLevelInts.includes(l));
        newLevel.levels.forEach(l => { if (!newLevels.includes(l)) newLevels.push(l); });
      }

      // прибираємо id поточної цілі, додаємо id нової (для topic-mode)
      if (newPurpose) {
        const oldPurposeId = learningPath?.purpose?.id;
        newPurposes = newPurposes.filter(p => p !== oldPurposeId);
        if (!newPurposes.includes(newPurpose.id)) newPurposes.push(newPurpose.id);
      }

      await axios.patch(
        '/api/v1/user-profile/current',
        { english_levels: newLevels, english_purposes: newPurposes },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
            ...acceptLangHeaders(),
          },
          withCredentials: true,
        }
      );

      // Якщо рівень/ціль не змінювались у цьому виклику — лишаємо поточні id з learningPath
      const finalLevelId = newLevel ? newLevel.id : learningPath?.level?.id;
      const finalPurposeId = newPurpose ? newPurpose.id : learningPath?.purpose?.id;
      setPendingRedirect(`/roadMap?level=${finalLevelId}&purpose=${finalPurposeId}`);

      setShowSuccess(true);
    } catch (err) {
      console.error('Error saving plan:', err);
    } finally {
      setDrawerSaving(false);
    }
  };

  // Loading
  if (loadingPath || loadingDetails) {
    return (
      <div className={css.roadMapPage}>
        <Header authUser={authUser} />
        <div style={{ padding: 60, textAlign: 'center', color: 'var(--t3)' }}>
          Завантаження курсу...
        </div>
      </div>
    );
  }

  // Error або немає params
  if (error || !learningPath) {
    return (
      <div className={css.roadMapPage}>
        <Header authUser={authUser} />
        <div style={{ padding: 60, textAlign: 'center' }}>
          <p style={{ color: 'var(--t2)', marginBottom: 16 }}>
            {error || 'Курс не знайдено'}
          </p>
          {!authUser?.isAuthenticated ? (
            <>
              <p style={{ color: 'var(--t3)', marginBottom: 24, fontSize: '14px' }}>
                {t('Complete the survey to see your personalized learning plan')}
              </p>
              <button
                onClick={() => navigate('/survey')}
                style={{
                  padding: '10px 24px', borderRadius: 10,
                  background: 'var(--i500)', color: '#fff',
                  border: 'none', cursor: 'pointer', fontWeight: 700,
                  marginRight: '12px'
                }}
              >
                {t('Take survey')}
              </button>
              <button
                onClick={() => navigate('/')}
                style={{
                  padding: '10px 24px', borderRadius: 10,
                  background: 'transparent', color: 'var(--t2)',
                  border: '1px solid var(--b2)', cursor: 'pointer', fontWeight: 700
                }}
              >
                {t('Back to home')}
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate('/mylearning')}
              style={{
                padding: '10px 24px', borderRadius: 10,
                background: 'var(--i500)', color: '#fff',
                border: 'none', cursor: 'pointer', fontWeight: 700
              }}
            >
              ← {t('My courses')}
            </button>
          )}
        </div>
      </div>
    );
  }

  const courseName = learningPath.name;
  const levelLabel = `${learningPath.level.option}`;
  const purposeLabel = learningPath.purpose.purpose;
  const totalLessons = allLessons.length;

  // Похідні значення для шторки — рахуємо тут, бо тепер learningPath і drawerLevels гарантовано існують
  const currentLevelId = learningPath?.level?.id;
  const currentLevelIndex = drawerLevels.findIndex(l => l.id === currentLevelId);

  const selectedLevelObj = drawerLevels.find(l => l.id === selLevel) || null;
  const selectedPurposeObj = drawerPurposes.find(p => p.id === selTopic) || null;

  return (
    <div className={css.roadMapPage}>
      <Header authUser={authUser} />

      {/* HERO */}
      <section className={css.hero}>
        <div className={`${css.heroOrb} ${css.o1}`}></div>
        <div className={`${css.heroOrb} ${css.o2}`}></div>
        <div className={css.heroInner}>
          <div className={css.courseHead}>
            <div className={css.courseInfo}>
                    <div className={css.courseBadge}>
                      {t(levelLabel)} · {t(purposeLabel)}
                    </div>
                    <h1 className={css.courseTitle}>{t(courseName)}</h1>
                    <div className={css.courseMeta}>
                      {totalLessons} {t('lessons')}
                    </div>
                  </div>

            {/* Manage dropdown - only for authenticated users */}
            {authUser?.isAuthenticated && (
              <div className={css.manageWrap} ref={manageRef}>
                <button
                  className={`${css.btnManage} ${dropdownOpen ? css.open : ''}`}
                  onClick={() => setDropdownOpen(prev => !prev)}
                >
                  <DotsIcon />
                  {t("Manage plan")}
                </button>

                <div className={`${css.dropdown} ${dropdownOpen ? css.show : ''}`}>
                  <div className={css.ddItem} onClick={() => openPlanDrawer('up')}><ChevronUp /> {t('Change level: higher')}</div>
                  <div className={css.ddItem} onClick={() => openPlanDrawer('down')}><ChevronDown /> {t('Change level: lower')}</div>
                  <div className={css.ddSep}></div>
                  <div className={css.ddItem} onClick={() => openPlanDrawer('topic')}><BookIcon /> {t('Change learning topic')}</div>
                  <div className={css.ddSep}></div>
                  <div className={`${css.ddItem} ${css.soon}`}>
                    <UserIcon /> {t('Study with tutor')}
                    <span className={css.ddSoon}>{t('Soon')}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Drawer */}
            <div className={`${css.drOv} ${planDrawerOpen ? css.show : ''}`} onClick={closePlanDrawer}></div>
            <div className={`${css.dr} ${planDrawerOpen ? css.show : ''}`}>

              {/* SUCCESS SCREEN */}
              {showSuccess ? (
                <div className={`${css.drSuc} ${css.show}`}>
                  <div className={css.drSucIcon}><CheckIcon /></div>
                  <div className={css.drSucH}>План оновлено!</div>
                  <div className={css.drSucP}>
                    {selectedPurposeObj ? `${selectedPurposeObj.emoji || ''} ${t(selectedPurposeObj.purpose)}` : t(purposeLabel)}
                    {' '}
                    {selectedLevelObj ? t(selectedLevelObj.option) : t(levelLabel)}
                    {' '}застосовано до вашого курсу.
                  </div>
                  <div className={css.drSucBtns}>
                    <button
                      className={`${css.drBtn} ${css.drBtnP}`}
                      onClick={() => {
                        setPlanDrawerOpen(false);
                        setShowSuccess(false);
                        if (pendingRedirect) navigate(pendingRedirect);
                      }}
                    >
                      Перейти до курсу <ArrowRight />
                    </button>
                    {authUser?.isAuthenticated && (
                      <button
                        className={`${css.drBtn} ${css.drBtnS}`}
                        onClick={() => { setPlanDrawerOpen(false); setShowSuccess(false); navigate('/mylearning'); }}
                      >
                        Повернутися до моїх курсів
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <>
                                    {/* UP MODE */}
                  {manageMode === 'up' && (
                    <>
                      <div className={css.drHead}>
                        <div className={css.drTop}>
                          <div className={css.drTitle}>{t("Change level: higher")}</div>
                          <div className={css.drClose} onClick={closePlanDrawer}><CloseIcon /></div>
                        </div>
                        <div className={css.drSub}>{t("Choose the level for the next course. We will change your current plan.")}</div>
                        <div className={css.drCur}>
                          <div className={css.drCurLabel}>{t("Current course")}</div>
                          <div className={css.drCurTitle}>{t(courseName)}</div>
                          <div className={css.drCurLevel}>{t(levelLabel)}</div>
                        </div>
                      </div>
                      <div className={css.drBody}>
                        {drawerLoading ? (
                          <div>{t("Loading...")}</div>
                        ) : (
                          <div className={css.drOpts}>
                            {drawerLevels.map((l, i) => {
                              if (currentLevelIndex === -1 || i <= currentLevelIndex) return null;
                              const rec = i === currentLevelIndex + 1;
                              return (
                                <div key={l.id} className={`${css.rc} ${selLevel === l.id ? css.sel : ''}`} onClick={() => setSelLevel(l.id)}>
                                  <div className={css.rcR}></div>
                                  <div className={css.rcBody}>
                                    <div className={css.rcTitle}>{t(l.option)}</div>
                                    {rec && <div className={css.rcBadge}>{t("Recommended")}</div>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <div className={css.drLink}><InfoIcon /> {t("Not sure? Take a short test")}</div>
                      </div>
                      <div className={css.drFoot}>
                        <button className={`${css.drBtn} ${css.drBtnS}`} onClick={closePlanDrawer}>{t("Cancel")}</button>
                        <button
                          className={`${css.drBtn} ${css.drBtnP} ${selLevel === null || drawerSaving ? css.dis : ''}`}
                          disabled={selLevel === null || drawerSaving}
                          onClick={() => changePlan({ newLevel: selectedLevelObj })}
                        >
                          {drawerSaving ? t("Saving...") : t("Change level")} <ArrowRight />
                        </button>
                      </div>
                    </>
                  )}

                  {/* DOWN MODE */}
                  {manageMode === 'down' && (
                    <>
                      <div className={css.drHead}>
                        <div className={css.drTop}>
                          <div className={css.drTitle}>{t("Change level: lower")}</div>
                          <div className={css.drClose} onClick={closePlanDrawer}><CloseIcon /></div>
                        </div>
                        <div className={css.drSub}>{t("Choose a course that better matches your current level. We will change your current plan.")}</div>
                        <div className={css.drCur}>
                          <div className={css.drCurLabel}>{t("Current course")}</div>
                          <div className={css.drCurTitle}>{t(courseName)}</div>
                          <div className={css.drCurLevel}>{t(levelLabel)}</div>
                        </div>
                      </div>
                      <div className={css.drBody}>
                        {drawerLoading ? (
                          <div>{t("Loading...")}</div>
                        ) : (
                          <div className={css.drOpts}>
                            {drawerLevels.map((l, i) => {
                              if (currentLevelIndex === -1 || i >= currentLevelIndex) return null;
                              const rec = i === currentLevelIndex - 1;
                              return (
                                <div key={l.id} className={`${css.rc} ${selLevel === l.id ? css.sel : ''}`} onClick={() => setSelLevel(l.id)}>
                                  <div className={css.rcR}></div>
                                  <div className={css.rcBody}>
                                    <div className={css.rcTitle}>{t(l.option)}</div>
                                    {rec && <div className={css.rcBadge}>{t("Recommended")}</div>}
                                  </div>
                                </div>
                              );
                            }).reverse()}
                          </div>
                        )}
                        <div className={css.drLink}><InfoIcon /> {t("Not sure? Take a short test")}</div>
                      </div>
                      <div className={css.drFoot}>
                        <button className={`${css.drBtn} ${css.drBtnS}`} onClick={closePlanDrawer}>{t("Cancel")}</button>
                        <button
                          className={`${css.drBtn} ${css.drBtnP} ${selLevel === null || drawerSaving ? css.dis : ''}`}
                          disabled={selLevel === null || drawerSaving}
                          onClick={() => changePlan({ newLevel: selectedLevelObj })}
                        >
                          {drawerSaving ? t("Saving...") : t("Change level")} <ArrowRight />
                        </button>
                      </div>
                    </>
                  )}

                  {/* TOPIC MODE */}
                  {manageMode === 'topic' && (
                    <>
                      {topicStep === 1 ? (
                        <>
                          <div className={css.drHead}>
                            <div className={css.drTop}>
                              <div className={css.drTitle}>{t("Choose a new topic")}</div>
                              <div className={css.drClose} onClick={closePlanDrawer}><CloseIcon /></div>
                            </div>
                            <div className={css.drStep}>{t("Step 1 of 2")}</div>
                            <div className={css.drSub}>{t("We will change the goal of your current course.")}</div>
                            <div className={css.drCur}>
                              <div className={css.drCurLabel}>{t("Current course")}</div>
                              <div className={css.drCurTitle}>{t(courseName)}</div>
                              <div className={css.drCurLevel}>{t(levelLabel)} · {t(purposeLabel)}</div>
                            </div>
                          </div>
                          <div className={css.drBody}>
                            {drawerLoading ? (
                              <div>{t("Loading...")}</div>
                            ) : (
                              <div className={css.drOpts}>
                                {drawerPurposes.map(p => (
                                  <div key={p.id} className={`${css.tc} ${selTopic === p.id ? css.sel : ''}`} onClick={() => setSelTopic(p.id)}>
                                    <div className={css.tcEmoji}>{p.emoji || '📘'}</div>
                                    <div className={css.tcBody}>
                                      {/* Динамічний переклад теми */}
                                      <div className={css.tcTitle}>{t(p.purpose)}</div>
                                    </div>
                                    <div className={css.tcCheck}><CheckIcon /></div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className={css.drFoot}>
                            <button className={`${css.drBtn} ${css.drBtnS}`} onClick={closePlanDrawer}>{t("Cancel")}</button>
                            <button className={`${css.drBtn} ${css.drBtnP} ${selTopic === null ? css.dis : ''}`} onClick={() => setTopicStep(2)}>
                              {t("Next: choose level")} <ArrowRight />
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className={css.drHead}>
                            <div className={css.drTop}>
                              <div className={css.drTitle}>{t("Which level to start from?")}</div>
                              <div className={css.drClose} onClick={closePlanDrawer}><CloseIcon /></div>
                            </div>
                            <div className={css.drStep}>{t("Step 2 of 2")}</div>
                            <div className={css.drSub}>{t("Choose a level for the new topic.")}</div>
                          </div>
                          <div className={css.drBody}>
                            {drawerLoading ? (
                              <div>{t("Loading...")}</div>
                            ) : (
                              <div className={css.drOpts}>
                                {drawerLevels.map((l) => {
                                  const rec = l.id === currentLevelId;
                                  return (
                                    <div key={l.id} className={`${css.rc} ${selLevel === l.id ? css.sel : ''}`} onClick={() => setSelLevel(l.id)}>
                                      <div className={css.rcR}></div>
                                      <div className={css.rcBody}>
                                        {/* Динамічний переклад рівня */}
                                        <div className={css.rcTitle}>{t(l.option)}</div>
                                        {rec && <div className={css.rcBadge}>{t("Recommended")}</div>}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            <div className={css.drLink}><InfoIcon /> {t("Not sure? Take a test")}</div>
                            <div className={css.drSummary}>
                              <div className={css.drSummaryTitle}>{t("Updated plan")}</div>
                              <div className={css.drSummaryRow}>
                                <span className={css.sl}>{t("Topic")}</span>
                                <span className={css.sv}>{selectedPurposeObj ? `${selectedPurposeObj.emoji || ''} ${t(selectedPurposeObj.purpose)}` : '-'}</span>
                              </div>
                              <div className={css.drSummaryRow}>
                                <span className={css.sl}>{t("Level")}</span>
                                <span className={css.sv}>{selectedLevelObj ? t(selectedLevelObj.option) : '-'}</span>
                              </div>
                            </div>
                            <div className={css.drInfo}><InfoIcon /> {t("Your progress on the current course will be saved, only the topic/level of the plan will change.")}</div>
                          </div>
                          <div className={css.drFoot}>
                            <button className={`${css.drBtn} ${css.drBtnS}`} onClick={() => setTopicStep(1)}>{t("Back")}</button>
                            <button
                              className={`${css.drBtn} ${css.drBtnP} ${selLevel === null || drawerSaving ? css.dis : ''}`}
                              disabled={selLevel === null || drawerSaving}
                              onClick={() => changePlan({ newLevel: selectedLevelObj, newPurpose: selectedPurposeObj })}
                            >
                              {drawerSaving ? t("Saving...") : t("Change course")} <ArrowRight />
                            </button>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          <div className={css.statsRow}>
            <div className={css.statCard}>
              <div className={css.statValue}>{totalLessons}</div>
              <div className={css.statLabel}>{t("Total lessons")}</div>
            </div>
            <div className={css.statCard}>
              <div className={css.statValue}>{lessonsWithStatus.filter(l => l.status === 'done').length}</div>
              <div className={css.statLabel}>{t("Completed lessons")}</div>
            </div>
            <div className={css.statCard}>
              <div className={css.statValue}>{completedWordsLoading ? '…' : completedWordsCount}</div>
              <div className={css.statLabel}>{t("Words learned")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTINUE CARD */}
      {currentLesson && (
        <div className={css.continueSection}>
          <div className={css.continueCard}>
            <div className={css.ccLeft}>
              <div className={css.ccTag}><span className={css.pulse}></span> {t("Continue Learning")}</div>
              <div className={css.ccTitle}>{currentLesson.name}</div>
              {currentLesson.topicName && (
                <div className={css.ccLesson}>{currentLesson.topicName}</div>
              )}
            </div>
            <div className={css.ccRight}>
              <button
                className={css.btnContinue}
                onClick={() => navigate(`/lesson/${currentLesson.id}`, { state: { from: 'roadMap' } })}
              >
                {t("Continue Learning")} <ArrowRight />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LEARNING PATH — по топіках */}
      <section className={css.pathSection}>
        <div className={css.pathHeader}>
          <h2 className={css.pathTitle}>{t("Learning path")}</h2>
          <div className={css.pathRule}><InfoIcon /> {t("The next lesson unlocks after you pass the previous one.")}</div>
        </div>

        {/* Warning for unauthenticated users */}
        {!authUser?.isAuthenticated && pathDetails?.topics?.length > 0 && (
          <div className={css.warningBanner}>
            <div className={css.warningIcon}>⚠️</div>
            <div className={css.warningContent}>
              <div className={css.warningTitle}>
                {t('Progress is not saved without registration')}
              </div>
              <div className={css.warningText}>
                {t('You can try lessons and use vocabulary, but to track progress and complete the course, please register.')}
              </div>
            </div>
            <button
              onClick={() => navigate('/signup')}
              className={css.warningButton}
            >
              {t('Register')}
            </button>
          </div>
        )}

        {/* Show lessons for both authenticated and unauthenticated users if data is available */}
        {pathDetails?.topics?.length > 0 ? (
          <div className={css.timeline}>
            {pathDetails.topics.map((topic) => (
              <div key={topic.id}>
                {topic.lessons.map((lesson) => {
                  const l = lessonsWithStatus.find(x => x.id === lesson.id) || { num: '', status: 'locked', score: null };
                  const isDone = l.status === 'done';
                  const isCurrent = l.status === 'current';
                  const isLocked = l.status === 'locked';

                  return (
                    <div className={css.lessonRow} key={lesson.id}>
                      <div className={css.lessonNode}>
                        <div className={`${css.nodeDot} ${isDone ? css.done : isCurrent ? css.current : css.locked}`}>
                          {isDone && <CheckIcon />}
                          {isLocked && <LockIcon />}
                        </div>
                        <span className={css.nodeNum}>{l.num}</span>
                      </div>

                      <div className={`${css.lessonCard} ${isCurrent ? css.currentCard : ''} ${isLocked ? css.locked : ''}`}>
                        {isLocked && <div className={css.lcTooltip}>🔒 {t("Available after completing the lesson")} {l.num - 1}</div>}
                        {isCurrent && <span className={css.currentMarker}>📍 {t("You are here")}</span>}

                        <div className={css.lcTop}>
                          <span className={css.lcTitle}>{lesson.name}</span>
                        </div>
                        <div className={css.lcDesc}>{topic.name}</div>

                        <div className={css.lcBottom}>
                          {isDone && <span className={`${css.lcStatus} ${css.stDone}`}><CheckIcon /> {t("Completed")}</span>}
                          {isCurrent && <span className={`${css.lcStatus} ${css.stCurrent}`}><ClockIcon /> {t("In Progress")}</span>}
                          {isLocked && <span className={`${css.lcStatus} ${css.stLocked}`}><LockIcon /> {t("Blocked")}</span>}

                          {isDone && l.score !== null && <span className={css.lcScore}>{l.score}%</span>}

                          {/* Words button - available for everyone */}
                          {isDone && (
                            <button
                              className={`${css.lcAction} ${css.actWords}`}
                              onClick={(e) => { e.stopPropagation(); openWords(l.num, lesson.name, lesson.id); }}
                            >
                              <BookIcon /> {t("Words")}
                            </button>
                          )}
                          
                          {/* Start/Continue button - available for everyone */}
                          {isCurrent && (
                            <button
                              className={`${css.lcAction} ${css.actContinue}`}
                              onClick={() => navigate(`/lesson/${lesson.id}`, { state: { from: 'roadMap' } })}
                            >
                              {t("Continue")} <ArrowRight />
                            </button>
                          )}
                          {isLocked && (
                            <span className={css.lcLockedMsg}><LockIcon /> {t("Complete previous lesson")}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ) : !authUser?.isAuthenticated ? (
          // Fallback: Show registration prompt only if no lesson data is available
          <div className={css.regPrompt}>
            <div className={css.regPromptIcon}>🎓</div>
            <h3 className={css.regPromptTitle}>
              {t('Register to access full learning path')}
            </h3>
            <p className={css.regPromptText}>
              {t('Create an account to unlock structured lessons, track your progress, and learn step by step.')}
            </p>
            <div className={css.regPromptButtons}>
              <button
                onClick={() => navigate('/signup')}
                className={css.regPromptBtnPrimary}
              >
                {t('Create free account')}
              </button>
              <button
                onClick={() => navigate('/trial-lesson')}
                className={css.regPromptBtnSecondary}
              >
                {t('Try free lesson')}
              </button>
            </div>
          </div>
        ) : (
          // For authenticated users with no lessons
          <div className={css.emptyState}>
            {t('No lessons available for this course')}
          </div>
        )}

        <div className={css.selfStudy}>
          <p>{t("Would you like to learn without a fixed plan?")}</p>
          <a href="/allvideos">{t("Go to self-study")} <ArrowRight /></a>
        </div>
      </section>

      {/* ===== WORDS DRAWER ===== */}
      <div className={`${css.wdOv} ${wordsDrawerOpen ? css.show : ''}`} onClick={closeWords}></div>
      <div className={`${css.wdDrawer} ${wordsDrawerOpen ? css.show : ''}`}>
        <div className={css.wdHead}>
          <div className={css.wdHeadL}>
            <div className={css.wdHeadIcon}><BookIcon /></div>
            <div>
              <div className={css.wdTitle}>{t("Lesson words")} {wordsDrawerLesson?.num}</div>
              <div className={css.wdSub}>
                {wordsDrawerLesson ? `${wordsDrawerLesson.title} · ${wordsDrawerWords.length} ${t("words")}` : ''}
              </div>
            </div>
          </div>
          <button className={css.wdClose} onClick={closeWords}><CloseIcon /></button>
        </div>
        <div className={css.wdList}>
          {wordsDrawerLoading ? (
            <div className={css.wdEmpty}>{t("Loading...")}</div>
          ) : wordsDrawerWords.length === 0 ? (
            <div className={css.wdEmpty}>{t("No words for this lesson")}</div>
          ) : (
            wordsDrawerWords.map((w) => (
              <div className={css.wdItem} key={w.id}>
                <div className={css.wdItemTop}>
                  <span className={css.wdEn}>{w.text}</span>
                  <span className={css.wdUa}>{w.translation}</span>
                </div>
                {w.usage_example && <div className={css.wdEx}>"{w.usage_example}"</div>}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default RoadMap;